/**
 * How a model is described to the person using it.
 *
 * The picker sits directly above the design agent's composer — it is one of
 * the first controls a creator meets. It used to read
 * `Claude Fable 5.1 — no forced tool calls` and, in Settings,
 * `· $3 / $15 per Mtok`: three pieces of API jargon in one dropdown. The tier
 * is the only thing a creator actually chooses on, so that is what the option
 * says; the price, where it is shown at all, goes on a muted second line in
 * units someone can read.
 *
 * Kept as a pure module so the dock and the Settings card can never drift
 * apart on the wording.
 */

export type ModelTier = "flagship" | "balanced" | "fast";

/** A model's tier, in the words a creator picks on. */
export function tierWord(tier: ModelTier): string {
  return tier === "flagship" ? "Best quality" : tier === "balanced" ? "Balanced" : "Fastest";
}

/**
 * The text of one `<option>` in a model picker.
 *
 * `forcedToolUse: false` means the model answers 400 to the forced tool call
 * every structured request in this codebase makes — it would fail at the first
 * message, not degrade. It stays listed (so nobody hunts for a model that is
 * simply not usable here) and says why, without naming the mechanism.
 */
export function modelOptionLabel(m: { label: string; tier: ModelTier; forcedToolUse: boolean }): string {
  return m.forcedToolUse
    ? `${m.label} — ${tierWord(m.tier)}`
    : `${m.label} — Not available for the design agent`;
}

/** Per-million-token prices, as a sentence. Null prices → no line at all: a
 *  wrong number is worse than an honest blank (see `modelCatalog.ts`). */
export function modelPriceLine(m: { inputUsdPerM: number | null; outputUsdPerM: number | null }): string | null {
  if (m.inputUsdPerM === null || m.outputUsdPerM === null) return null;
  return `$${m.inputUsdPerM} in / $${m.outputUsdPerM} out per 1M tokens`;
}
